import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { requestLeave, getLeaves } from "../../api/attendanceService";
import apiClient from "../../api/apiClient";
import useAuthStore from "../../store/authStore";


const _balanceCache = {};

const HALF_DAY_TYPES = new Set(["HALF DAY", "HALF CASUAL"]);

function RequestLeave({ user = null, onClose }) {
  const navigate = useNavigate();
  const role = useAuthStore((state) => state.role);
  const authFullName = useAuthStore((state) => state.fullName);
  const authEmployeeId = useAuthStore((state) => state.employeeId);

  const targetUser = user
    ? { full_name: user.full_name, employee_id: user.employee_id, isDifferent: true }
    : {
      full_name: authFullName || "Employee",
      employee_id: authEmployeeId,
      isDifferent: false,
    };

  const oneMonthAgo = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toLocaleDateString("en-CA");
  })();

  const [balanceData, setBalanceData] = useState({
    policy_limit: 0, casual_taken: 0, fy_limit: 0,
    fy_taken: 0, available: 0, loaded: false,
  });
  const [compOffBalance, setCompOffBalance] = useState({ available: 0, loaded: false });
  const [medicalLeaveData, setMedicalLeaveData] = useState({
    year: new Date().getFullYear(),
    allowed: true,
    used: false,
    days_taken: "0",
    status: null,
    max_days: 6,
    loaded: false,
  });

  const [leaveType, setLeaveType] = useState("");
  const [halfDaySession, setHalfDaySession] = useState(""); // ← NEW
  const [leaveFrom, setLeaveFrom] = useState("");
  const [leaveTo, setLeaveTo] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [submitOk, setSubmitOk] = useState(false);

  // Medical file state
  const [medicalFile, setMedicalFile] = useState(null);
  const fileInputRef = useRef(null);

  const [managers, setManagers] = useState([]);
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [managerSearch, setManagerSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const abortRef = useRef(null);

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const now = new Date();
    const cacheKey = `${targetUser.employee_id}_${now.getFullYear()}_${now.getMonth()}`;
    if (_balanceCache[cacheKey]) {
      setBalanceData({ ..._balanceCache[cacheKey], loaded: true });
    }

    (async () => {
      try {
        const [res, coRes] = await Promise.all([
          getLeaves({
            employee_id: targetUser.isDifferent ? targetUser.employee_id : undefined,
            year: now.getFullYear(),
            month: now.getMonth() + 1,
          }),
          apiClient.get(
            targetUser.isDifferent
              ? `/attendance/api/comp-off/${targetUser.employee_id}/`
              : "/attendance/api/comp-off/"
          ).catch(() => ({ data: { available: 0 } }))
        ]);

        if (controller.signal.aborted) return;

        const clBalance = res.data.cl_balance;
        if (clBalance && typeof clBalance.available === "number") {
          const fresh = {
            policy_limit: clBalance.policy_limit ?? 0,
            casual_taken: clBalance.casual_taken ?? 0,
            fy_limit: clBalance.fy_limit ?? 0,
            fy_taken: clBalance.fy_taken ?? 0,
            available: clBalance.available,
          };
          _balanceCache[cacheKey] = fresh;
          setBalanceData({ ...fresh, loaded: true });
        } else {
          setBalanceData(prev => ({ ...prev, loaded: true }));
        }

        setCompOffBalance({ available: coRes.data.available ?? 0, loaded: true });

        if (res.data.medical_leave) {
          setMedicalLeaveData({ ...res.data.medical_leave, loaded: true });
        } else {
          setMedicalLeaveData(prev => ({ ...prev, loaded: true }));
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        setBalanceData(prev => ({ ...prev, loaded: true }));
        setCompOffBalance(prev => ({ ...prev, loaded: true }));
        setMedicalLeaveData(prev => ({ ...prev, loaded: true }));
      }
    })();

    return () => controller.abort();
  }, [targetUser.employee_id, targetUser.isDifferent]);

  useEffect(() => {
    (async () => {
      try {
        const params = {};
        if (targetUser.employee_id) {
          params.employee_id = targetUser.employee_id;
        }
        const res = await apiClient.get("/attendance/api/managers/", { params });
        const currentUserEmployeeId = useAuthStore.getState().employeeId;
        const filtered = res.data.filter(
          m =>
            m.employee_id !== currentUserEmployeeId &&
            m.employee_id !== targetUser.employee_id
        );
        setManagers(filtered);
      } catch (err) {
        console.error("Failed to load managers", err);
      }
    })();
  }, [targetUser.employee_id]);

  const LEAVE_TYPES = [
    { code: "CASUAL", name: "Casual Leave", days: null, isSingleDay: false, isCL: true, isCompOff: false, isMedical: false, icon: "" },
    { code: "HALF CASUAL", name: "Half Casual", days: 0.5, isSingleDay: true, isCL: true, isCompOff: false, isMedical: false, icon: "" },
    { code: "SICK", name: "Unpaid Full Day", days: null, isSingleDay: false, isCL: false, isCompOff: false, isMedical: false, icon: "" },
    { code: "HALF DAY", name: "Unpaid Half Day", days: 0.5, isSingleDay: true, isCL: false, isCompOff: false, isMedical: false, icon: "" },
    { code: "COMP_OFF", name: "Comp Off", days: null, isSingleDay: false, isCL: false, isCompOff: true, isMedical: false, icon: "" },
    // { code: "MEDICAL", name: "Medical Leave", days: null, isSingleDay: false, isCL: false, isCompOff: false, isMedical: true, icon: "" },
  ];

  const selectedType = LEAVE_TYPES.find(t => t.code === leaveType) || null;
  const isHalfDay = selectedType ? HALF_DAY_TYPES.has(selectedType.code) : false;

  const getCasualMaxTo = () => {
    if (!leaveFrom || !balanceData.loaded) return undefined;
    const available = Math.floor(balanceData.available);
    if (available <= 0) return leaveFrom;
    const from = new Date(leaveFrom);
    from.setDate(from.getDate() + available - 1);
    return from.toLocaleDateString("en-CA");
  };

  const getCompOffHint = () => {
    if (!compOffBalance.loaded) return "Loading your comp-off balance...";
    const avail = compOffBalance.available;
    if (avail <= 0) return "No comp-off balance remaining.";
    return `${avail} comp-off day${avail !== 1 ? "s" : ""} available.`;
  };

  const getCasualHint = () => {
    if (!balanceData.loaded) return "Loading your CL balance...";
    const avail = balanceData.available;
    if (avail <= 0) return "No CL balance remaining.";
    if (avail === 1) return `1 CL available — you can apply for 1 day.`;
    return `${avail} CL available — you can apply for up to ${avail} consecutive days.`;
  };

  const getMedicalHint = () => {
    if (!medicalLeaveData.loaded) return "Checking Medical Leave status...";
    if (!medicalLeaveData.allowed) {
      return `Medical Leave has already been utilized for calendar year ${medicalLeaveData.year} (Status: ${medicalLeaveData.status || "Used"}). Only 1 Medical Leave permitted per calendar year.`;
    }
    return "Medical Leave: Up to 6 working days once per calendar year (1 Jan - 31 Dec). Mandatory prescription / medical certificate upload required.";
  };

  const isTypeDisabled = (type) => {
    if (type.isMedical) {
      if (!medicalLeaveData.loaded) return false;
      return !medicalLeaveData.allowed;
    }
    if (type.isCompOff) {
      if (!compOffBalance.loaded) return false;
      return compOffBalance.available < 1;
    }
    if (!type.isCL) return false;
    if (!balanceData.loaded) return false;
    if (type.code === "CASUAL" && balanceData.available < 1) return true;
    if (type.code === "HALF CASUAL" && balanceData.available < 0.5) return true;
    return false;
  };

  const handleLeaveTypeChange = (code) => {
    setLeaveType(code);
    setHalfDaySession(""); // reset session when type changes
    setLeaveFrom("");
    setLeaveTo("");
    setMedicalFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setErrorMsg("");
  };

  const handleLeaveFromChange = (val) => {
    setLeaveFrom(val);
    setErrorMsg("");
    if (selectedType?.isSingleDay) {
      setLeaveTo(val);
    } else if (leaveType === "CASUAL") {
      setLeaveTo(val);
    } else {
      if (leaveTo && leaveTo < val) setLeaveTo(val);
    }
  };

  const handleLeaveToChange = (val) => {
    if (selectedType?.isSingleDay) return;
    setLeaveTo(val);
    setErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!leaveType) { setErrorMsg("Please select a leave type."); return; }

    // ── Validate half_day_session for half-day types ──
    if (isHalfDay && !halfDaySession) {
      setErrorMsg("Please select which half of the day you are taking leave for.");
      return;
    }

    if (!leaveFrom || !leaveTo) { setErrorMsg("Please select leave dates."); return; }
    if (!reason.trim()) { setErrorMsg("Please provide a reason."); return; }

    if (leaveFrom < oneMonthAgo) {
      setErrorMsg("Leave cannot be applied for dates more than 1 month in the past.");
      return;
    }

    if (leaveType === "MEDICAL") {
      if (!medicalFile) {
        setErrorMsg("Please upload your medical certificate or doctor prescription.");
        return;
      }
    }

    if (leaveTo < leaveFrom) { setErrorMsg("Leave to date cannot be before leave from date."); return; }

    if (leaveType === "CASUAL" && balanceData.loaded && balanceData.available < 1) {
      setErrorMsg(`Insufficient Casual Leave. Available: ${balanceData.available} days.`); return;
    }
    if (leaveType === "HALF CASUAL" && balanceData.loaded && balanceData.available < 0.5) {
      setErrorMsg(`Insufficient Casual Leave for half day. Available: ${balanceData.available} days.`); return;
    }
    if (leaveType === "COMP_OFF" && compOffBalance.loaded && compOffBalance.available < 1) {
      setErrorMsg(`Insufficient Comp-Off balance. Available: ${compOffBalance.available} days.`); return;
    }

    setSubmitting(true);
    try {
      if (leaveType === "MEDICAL") {
        const formData = new FormData();
        formData.append("leave_date_from", leaveFrom);
        formData.append("leave_date_to", leaveTo);
        formData.append("leave_type", leaveType);
        formData.append("reason", reason.trim());
        formData.append("medical_document", medicalFile);
        if (selectedManagers.length > 0) {
          formData.append("additional_managers", JSON.stringify(selectedManagers.map(m => m.employee_id)));
        }
        if (targetUser.isDifferent) formData.append("employee_id", targetUser.employee_id);
        await requestLeave(formData);
      } else {
        const payload = {
          leave_date_from: leaveFrom,
          leave_date_to: leaveTo,
          leave_type: leaveType,
          reason: reason.trim(),
          additional_managers: selectedManagers.map(m => m.employee_id),
        };

        // ── Only send half_day_session for half-day leave types ──
        if (isHalfDay && halfDaySession) {
          payload.half_day_session = halfDaySession;
        }

        if (targetUser.isDifferent) payload.employee_id = targetUser.employee_id;

        await requestLeave(payload);
      }

      const now = new Date();
      const cacheKey = `${targetUser.employee_id}_${now.getFullYear()}_${now.getMonth()}`;
      delete _balanceCache[cacheKey];

      setLeaveFrom("");
      setLeaveTo("");
      setLeaveType("");
      setHalfDaySession("");
      setReason("");
      setMedicalFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setSelectedManagers([]);
      setManagerSearch("");
      setDropdownOpen(false);
      setSubmitOk(true);
      setTimeout(() => setSubmitOk(false), 3000);

      if (leaveType === "MEDICAL") {
        setMedicalLeaveData(prev => ({ ...prev, allowed: false, used: true }));
      }

      if (onClose) onClose();
      else setSubmitOk(true);
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Failed to submit leave request."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openLeaveHistory = () => {
    if (!targetUser.isDifferent) navigate(`/${role.toLowerCase()}/leave-report`);
    else navigate(`/${role.toLowerCase()}/leave-report/${targetUser.employee_id}`);
  };

  return (
    <>
      <style>{`
        select { background-color: transparent !important; color: white !important; appearance: none; }
        select option { background-color: #1e293b; color: white; }
        select option:disabled { color: #6b7280; }
        .leave-type-card { transition: all 0.15s ease; }
        .leave-type-card:active { transform: scale(0.97); }
        .balance-strip {
          display: flex; flex-direction: row; gap: 8px;
          overflow-x: auto; -webkit-overflow-scrolling: touch;
          scrollbar-width: none; padding-bottom: 2px;
        }
        .balance-strip::-webkit-scrollbar { display: none; }
        .balance-strip .bal-card { flex: 1 0 0; min-width: 88px; }
        .lt-strip {
          display: flex; flex-direction: row; gap: 8px;
          overflow-x: auto; -webkit-overflow-scrolling: touch;
          scrollbar-width: none; padding-bottom: 2px;
        }
        .lt-strip::-webkit-scrollbar { display: none; }
        .lt-strip .lt-card { flex: 1 0 0; min-width: 72px; }
        .lt-card.selected-card {
          box-shadow: 0 0 0 2px rgba(168,85,247,0.6), 0 4px 20px rgba(168,85,247,0.25);
        }
        .session-btn { transition: all 0.15s ease; }
        .session-btn:active { transform: scale(0.97); }
        input[type="date"]:focus, textarea:focus {
          box-shadow: 0 0 0 3px rgba(168,85,247,0.2);
        }
        .section-divider {
          height: 1px;
          background: linear-gradient(to right, transparent, rgba(255,255,255,0.12), transparent);
          margin: 4px 0 16px;
        }
      `}</style>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="w-full max-w-4xl mx-auto px-2 sm:px-0"
      >
        <div className="relative bg-white/15 backdrop-blur-xl rounded-2xl border border-white/20 p-5 sm:p-7 md:p-9 shadow-2xl">
          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-[95] w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 flex items-center justify-center text-white/70 hover:text-white transition-all text-sm font-semibold cursor-pointer"
              type="button"
            >
              ✕
            </button>
          )}

          {/* ── HEADER ── */}
          <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg flex-shrink-0">
                <span className="text-2xl sm:text-3xl">🏖️</span>
              </div>
              <div>
                <h3 className="text-xl sm:text-3xl font-bold text-white leading-tight tracking-tight">Request Leave</h3>
                <p className="text-sm sm:text-base text-white/55 mt-0.5">Apply for time off</p>
              </div>
            </div>
            {!targetUser.isDifferent && (
              <motion.button
                whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                onClick={openLeaveHistory}
                className={`px-4 py-2.5 sm:px-6 sm:py-3 rounded-xl font-semibold text-sm sm:text-base bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-lg whitespace-nowrap ${onClose ? "mr-8 sm:mr-10" : ""
                  }`}
              >
                <span className="hidden sm:inline">View </span>Leave History
              </motion.button>
            )}
          </div>

          <div className="section-divider" />

          {/* ── BALANCE STRIP ── */}
          <div className="balance-strip mb-5">
            <MiniBalCard icon="" label="Employee" value={targetUser.full_name} sub={targetUser.employee_id} />
            <MiniBalCard icon="" label="Month Used" value={balanceData.loaded ? `${balanceData.casual_taken}d` : "—"} sub={balanceData.loaded ? `/ ${balanceData.policy_limit} lim` : "…"} />
            <MiniBalCard icon="" label="Avail. CL" value={balanceData.loaded ? `${balanceData.available}d` : "—"} sub="carry-fwd" highlight={balanceData.loaded && balanceData.available === 0} />
            <MiniBalCard icon="" label="Comp-Off" value={compOffBalance.loaded ? `${compOffBalance.available}d` : "—"} sub="exp Mar 31" highlight={compOffBalance.loaded && compOffBalance.available === 0} />
            {/* <MiniBalCard
              icon=""
              label="Medical"
              value={medicalLeaveData.loaded ? (medicalLeaveData.allowed ? "6d Avail" : "1/1 Used") : "—"}
              sub={medicalLeaveData.loaded && !medicalLeaveData.allowed ? `Status: ${medicalLeaveData.status || "Claimed"}` : "1x/yr (Jan-Dec)"}
              highlight={medicalLeaveData.loaded && !medicalLeaveData.allowed}
            /> */}
          </div>

          {/* ── SUCCESS TOAST ── */}
          {submitOk && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mb-5 px-4 py-3.5 rounded-xl bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-sm sm:text-base font-semibold flex items-center gap-2"
            >
              ✅ Leave request submitted successfully!
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">

            {/* ── LEAVE TYPE CARDS ── */}
            <div>
              <Label icon="">Leave Type</Label>
              <div className="lt-strip mt-2">
                {LEAVE_TYPES.map((lt) => {
                  const disabled = isTypeDisabled(lt);
                  const selected = leaveType === lt.code;
                  return (
                    <button
                      key={lt.code}
                      type="button"
                      disabled={disabled}
                      onClick={() => !disabled && handleLeaveTypeChange(lt.code)}
                      className={`lt-card leave-type-card relative flex flex-col items-center gap-1.5 px-2 py-3.5 rounded-xl border-2 text-center
                        ${selected
                          ? "border-purple-400 bg-purple-500/25 shadow-lg shadow-purple-500/20 selected-card"
                          : disabled
                            ? "border-white/10 bg-white/5 opacity-40 cursor-not-allowed"
                            : "border-white/20 bg-white/10 hover:border-purple-400/60 hover:bg-purple-500/10 cursor-pointer"
                        }`}
                    >
                      <span className="text-lg leading-none">{lt.icon}</span>
                      <span className={`text-xs sm:text-sm font-semibold leading-tight ${selected ? "text-purple-200" : "text-white/85"}`}>
                        {lt.name}
                      </span>
                      {(lt.isCL && !balanceData.loaded) || (lt.isCompOff && !compOffBalance.loaded) ? (
                        <span className="text-[10px] text-white/30 leading-none">…</span>
                      ) : disabled ? (
                        <span className="text-[10px] text-red-400/80 leading-none font-medium">exhausted</span>
                      ) : null}
                      {selected && (
                        <motion.div
                          layoutId="typeSelected"
                          className="absolute inset-0 rounded-xl border-2 border-purple-400 pointer-events-none"
                          transition={{ type: "spring", bounce: 0.2, duration: 0.25 }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              {selectedType && (
                <motion.p
                  initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
                  className="mt-2.5 text-sm text-purple-300/80 px-1"
                >
                  {
                    selectedType.code === "CASUAL" ? getCasualHint()
                      : selectedType.isCompOff ? getCompOffHint()
                        : selectedType.code === "MEDICAL" ? getMedicalHint()
                          : selectedType.code === "HALF DAY" ? "No CL deducted. Single day only."
                            : selectedType.code === "HALF CASUAL" ? "0.5 CL deducted. Single day only."
                              : selectedType.hint
                  }
                </motion.p>
              )}
            </div>

            {/* ── HALF DAY SESSION PICKER — shown only for half-day types ── */}
            <AnimatePresence>
              {isHalfDay && (
                <motion.div
                  key="session-picker"
                  initial={{ opacity: 0, y: -8, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, y: -8, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <Label icon="">Which half are you taking off?</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {[
                      { value: "HALF_DAY_FIRST", label: " First Half", sub: "Morning leave" },
                      { value: "HALF_DAY_SECOND", label: " Second Half", sub: "Afternoon leave" },
                    ].map((s) => (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => { setHalfDaySession(s.value); setErrorMsg(""); }}
                        className={`session-btn flex flex-col items-center gap-1 px-4 py-4 rounded-xl border-2 text-center transition-all
                          ${halfDaySession === s.value
                            ? "border-amber-400 bg-amber-500/20 shadow-lg shadow-amber-500/20"
                            : "border-white/20 bg-white/10 hover:border-amber-400/60 hover:bg-amber-500/10"
                          }`}
                      >
                        <span className="text-xl">{s.label.split(" ")[0]}</span>
                        <span className={`text-sm font-bold ${halfDaySession === s.value ? "text-amber-200" : "text-white/85"}`}>
                          {s.label.split(" ").slice(1).join(" ")}
                        </span>
                        <span className="text-xs text-white/50">{s.sub}</span>
                      </button>
                    ))}
                  </div>
                  {!halfDaySession && (
                    <p className="mt-2 text-xs text-amber-400/80 px-1">⚠️ Please select which half you are taking off</p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── DATES — only show once session is selected for half-day, or immediately for others ── */}
            {leaveType && (!isHalfDay || halfDaySession) && (
              <motion.div
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18 }}
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              >
                <div>
                  <Label icon="">Leave From</Label>
                  <input
                    type="date"
                    value={leaveFrom}
                    min={oneMonthAgo}
                    onChange={e => handleLeaveFromChange(e.target.value)}
                    required
                    className={inputClass}
                  />
                  <p className="mt-1 text-xs text-purple-300/80">
                    Past dates allowed: Up to 1 month exception
                  </p>
                </div>
                <div>
                  <Label icon="">Leave To</Label>
                  <input
                    type="date"
                    value={leaveTo}
                    min={leaveFrom || oneMonthAgo}
                    max={
                      leaveType === "CASUAL" ? getCasualMaxTo()
                        : selectedType?.isSingleDay ? leaveFrom
                          : undefined
                    }
                    onChange={e => handleLeaveToChange(e.target.value)}
                    disabled={selectedType?.isSingleDay}
                    required
                    className={`${inputClass} ${selectedType?.isSingleDay ? "opacity-50 cursor-not-allowed" : ""}`}
                  />
                  {selectedType?.isSingleDay && (
                    <p className="mt-1.5 text-sm text-white/40">Auto-set to same day</p>
                  )}
                  {leaveType === "CASUAL" && leaveFrom && balanceData.loaded && balanceData.available > 1 && (
                    <p className="mt-1.5 text-sm text-purple-300/70">
                      Max {Math.floor(balanceData.available)} day{Math.floor(balanceData.available) > 1 ? "s" : ""} from selected start date
                    </p>
                  )}
                  {leaveType === "MEDICAL" && leaveFrom && (
                    <p className="mt-1.5 text-xs text-purple-300/80">
                      Max 6 working days (holidays & week-offs excluded)
                    </p>
                  )}
                </div>
              </motion.div>
            )}

            {/* ── MEDICAL DOCUMENT UPLOAD (MANDATORY FOR MEDICAL LEAVE) ── */}
            <AnimatePresence>
              {leaveType === "MEDICAL" && (
                <motion.div
                  initial={{ opacity: 0, y: -6, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, y: -6, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="p-4 sm:p-5 rounded-2xl bg-purple-500/10 border-2 border-purple-500/30 space-y-3"
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <Label icon="">Upload Medical Certificate / Prescription *</Label>
                    <span className="text-xs text-purple-300 font-semibold bg-purple-500/20 px-2.5 py-0.5 rounded-full border border-purple-400/30">
                      Required • Max 5MB
                    </span>
                  </div>

                  {!medicalFile ? (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="cursor-pointer border-2 border-dashed border-purple-400/50 hover:border-purple-300 rounded-xl p-5 sm:p-6 text-center bg-white/5 hover:bg-purple-500/15 transition-all group"
                    >
                      <svg className="w-9 h-9 mx-auto mb-2 text-purple-300 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="text-sm sm:text-base font-bold text-white">Click to browse or drop medical certificate here</p>
                      <p className="text-xs text-white/50 mt-1">Accepted formats: PDF, JPG, PNG, WEBP (Max 5MB)</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) {
                            setErrorMsg("Selected file exceeds 5MB size limit.");
                            return;
                          }
                          setErrorMsg("");
                          setMedicalFile(file);
                        }}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-3.5 sm:p-4 rounded-xl bg-purple-950/60 border border-purple-400/40 gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <svg className="w-6 h-6 text-purple-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div className="min-w-0">
                          <p className="text-sm sm:text-base font-bold text-white truncate">{medicalFile.name}</p>
                          <p className="text-xs text-purple-300 font-mono">{(medicalFile.size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            const url = URL.createObjectURL(medicalFile);
                            window.open(url, "_blank");
                          }}
                          className="px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs sm:text-sm font-semibold flex items-center gap-1.5 transition active:scale-95 shadow cursor-pointer"
                        >
                          Preview
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMedicalFile(null);
                            if (fileInputRef.current) fileInputRef.current.value = "";
                          }}
                          className="w-8 h-8 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 flex items-center justify-center text-xs font-bold transition border border-red-500/30 cursor-pointer"
                          title="Remove document"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-purple-200/70 italic flex items-center gap-1.5">
                    <span>Medical Leave provides up to 6 working days once per calendar year (1 Jan - 31 Dec) verified by Reporting Manager or Admin.</span>
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── REASON ── */}
            {leaveType && (!isHalfDay || halfDaySession) && (
              <motion.div
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18, delay: 0.04 }}
              >
                <Label icon="">Reason</Label>
                <textarea
                  value={reason} onChange={e => setReason(e.target.value)}
                  rows="3" required placeholder="Please provide the reason..."
                  className={textareaClass}
                />
              </motion.div>
            )}

            {/* ── ADDITIONAL MANAGERS (BACKUP APPROVERS) ── */}
            {leaveType && (!isHalfDay || halfDaySession) && managers.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18, delay: 0.06 }}
                className="relative"
              >
                <Label icon="">Notify Additional Manager (Optional)</Label>

                {/* Selected Manager Chips */}
                {selectedManagers.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {selectedManagers.map(m => (
                      <span
                        key={m.employee_id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-500/20 border border-purple-400/40 text-purple-200 text-xs font-semibold shadow-sm"
                      >
                        <span>{m.full_name}</span>
                        <button
                          type="button"
                          onClick={() => setSelectedManagers(prev => prev.filter(x => x.employee_id !== m.employee_id))}
                          className="hover:text-red-400 text-[14px] leading-none focus:outline-none transition-colors ml-1 font-bold"
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Dropdown Input search */}
                <div className="relative">
                  <input
                    type="text"
                    value={managerSearch}
                    placeholder="Search by name, ID, or email..."
                    onFocus={() => setDropdownOpen(true)}
                    onChange={e => {
                      setManagerSearch(e.target.value);
                      setDropdownOpen(true);
                    }}
                    className={inputClass}
                  />
                  {dropdownOpen && (
                    <div className="absolute z-50 left-0 right-0 mt-2 max-h-60 overflow-y-auto rounded-xl bg-slate-900 border border-white/10 shadow-2xl p-2 space-y-1 scrollbar-thin scrollbar-thumb-purple-500">
                      {(() => {
                        const filtered = managers.filter(m => {
                          const query = managerSearch.toLowerCase();
                          const matches =
                            m.full_name.toLowerCase().includes(query) ||
                            m.employee_id.toLowerCase().includes(query) ||
                            m.email.toLowerCase().includes(query);
                          const isAlreadySelected = selectedManagers.some(s => s.employee_id === m.employee_id);
                          return matches && !isAlreadySelected;
                        });

                        if (filtered.length === 0) {
                          return (
                            <p className="text-white/40 text-xs py-3 text-center">
                              No matching managers found.
                            </p>
                          );
                        }

                        return filtered.map(m => (
                          <button
                            key={m.employee_id}
                            type="button"
                            onClick={() => {
                              setSelectedManagers(prev => [...prev, m]);
                              setManagerSearch("");
                              setDropdownOpen(false);
                            }}
                            className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-purple-500/25 border border-transparent hover:border-purple-500/30 transition-all flex flex-col gap-0.5"
                          >
                            <span className="text-white font-bold text-xs sm:text-sm">{m.full_name}</span>
                            <span className="text-white/40 text-[10px] sm:text-xs">
                              {m.employee_id} • {m.email} {m.department ? `• ${m.department}` : ""}
                            </span>
                          </button>
                        ));
                      })()}
                    </div>
                  )}
                </div>
                {dropdownOpen && (
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setDropdownOpen(false)}
                  />
                )}
              </motion.div>
            )}

            {/* ── ERROR ── */}
            {errorMsg && (
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                className="bg-red-500/20 border border-red-400/40 rounded-xl px-4 py-3.5"
              >
                <p className="text-red-300 text-sm sm:text-base font-medium">⚠️ {errorMsg}</p>
              </motion.div>
            )}

            {/* ── SUBMIT ── */}
            <div className="flex justify-center pt-1">
              <motion.button
                type="submit"
                disabled={submitting || !leaveType || (isHalfDay && !halfDaySession)}
                whileHover={{ scale: submitting ? 1 : 1.02 }}
                whileTap={{ scale: 0.97 }}
                className="w-full sm:w-80 py-4 sm:py-5 rounded-xl font-bold text-white text-base sm:text-lg
                  bg-gradient-to-r from-purple-500 to-pink-500
                  disabled:opacity-50 disabled:cursor-not-allowed
                  shadow-lg shadow-purple-500/25 transition-shadow"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    Submitting...
                  </span>
                ) : "🚀 Apply for Leave"}
              </motion.button>
            </div>
          </form>
        </div>
      </motion.div>
    </>
  );
}

const inputClass = "w-full mt-1.5 px-4 py-3.5 rounded-xl bg-white/15 border-2 border-purple-500/50 text-white text-sm sm:text-base outline-none focus:border-purple-400 transition-colors placeholder:text-white/30";
const textareaClass = "w-full mt-1.5 px-4 py-3.5 rounded-xl bg-white/15 border-2 border-purple-500/50 text-white text-sm sm:text-base outline-none resize-none focus:border-purple-400 transition-colors placeholder:text-white/30";

function Label({ children, icon }) {
  return (
    <p className="text-sm sm:text-base text-white/65 flex items-center gap-2 mb-1 font-semibold tracking-wide uppercase">
      <span>{icon}</span>{children}
    </p>
  );
}

function MiniBalCard({ icon, label, value, sub, highlight }) {
  return (
    <div className={`bal-card rounded-xl px-3 py-3 border text-center ${highlight ? "bg-red-500/10 border-red-400/30" : "bg-white/8 border-white/12"}`}>
      <div className="text-base leading-none mb-1">{icon}</div>
      <div className="text-[11px] sm:text-xs text-white/50 leading-tight truncate font-medium">{label}</div>
      <div className={`text-sm sm:text-base font-bold mt-1 truncate ${highlight ? "text-red-300" : "text-white"}`}>{value}</div>
      <div className="text-[10px] sm:text-[11px] text-white/40 leading-tight truncate mt-0.5">{sub}</div>
    </div>
  );
}

export default RequestLeave;
